import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { environment } from 'environments/environment';
import { catchError, Observable, of } from 'rxjs';
import {
    applyDocumentLanguage,
    getPersistedSaiLang,
    normalizeSaiLang,
    persistSaiLang,
    SaiLang,
} from './language-preference.util';

@Injectable({ providedIn: 'root' })
export class LanguagePreferenceService {
    private readonly _apiLink = environment.apiLink;
    private readonly _httpClient = inject(HttpClient);
    private readonly _translocoService = inject(TranslocoService);

    getPersisted(): SaiLang {
        return getPersistedSaiLang();
    }

    apply(lang: SaiLang): void {
        persistSaiLang(lang);
        this._translocoService.setActiveLang(lang);
        applyDocumentLanguage(lang);
    }

    applyFromBackend(prefLang: unknown): SaiLang | null {
        const lang = normalizeSaiLang(prefLang);
        if (!lang) {
            return null;
        }
        this.apply(lang);
        return lang;
    }

    /** Persist locally and notify the backend when the user is authenticated. */
    savePreferredLanguage(lang: SaiLang): Observable<unknown> {
        this.apply(lang);

        if (!this.isAuthenticated()) {
            return of(null);
        }

        return this._httpClient
            .put(`${this._apiLink}users/profile/pref-lang`, { prefLang: lang })
            .pipe(catchError(() => of(null)));
    }

    private isAuthenticated(): boolean {
        const token = localStorage.getItem('accessToken');
        return !!token && !AuthUtils.isTokenExpired(token);
    }
}
